import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PageHeader } from "@/components/Ui";
import { fmtMonthYear, istDateKey } from "@/lib/time";

// ── Helpers ────────────────────────────────────────────────────────────────────
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addDays(d, n)   { const nd = new Date(d); nd.setDate(d.getDate() + n); return nd; }

const DAY_FULL = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function fmtTime(when) {
  if (!when) return "";
  // Date-only strings (YYYY-MM-DD) have no meaningful time component — skip
  if (typeof when === "string" && !when.includes("T")) return "";
  const d = new Date(when);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
}

// Dot colours per event kind
const KIND_DOT = {
  hearing: "bg-primary",
  task:    "bg-emerald-400",
  case:    "bg-sky-400",
};
const KIND_LABEL = {
  hearing: "Hearing",
  task:    "Task",
  case:    "Filing",
};

export default function CalendarPage() {
  const navigate = useNavigate();

  const [anchor, setAnchor] = useState(startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => {
    const d = new Date(); d.setHours(0,0,0,0); return d;
  });

  const [events, setEvents] = useState([]);

  useEffect(() => {
    const start = istDateKey(addDays(anchor, -7));
    const end   = istDateKey(addDays(anchor, 42));
    api.get("/calendar", { params: { start, end } })
      .then(r => setEvents(r.data))
      .catch(() => setEvents([]));
  }, [anchor]);

  const cells = useMemo(() => {
    const offset    = anchor.getDay();
    const totalDays = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < offset; i++) arr.push(null);
    for (let d = 1; d <= totalDays; d++)
      arr.push(new Date(anchor.getFullYear(), anchor.getMonth(), d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [anchor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of events) {
      const day = istDateKey(e.when);
      if (!day) continue;
      (map[day] = map[day] || []).push(e);
    }
    return map;
  }, [events]);

  const today       = istDateKey(new Date());
  const selectedKey = istDateKey(selectedDay);
  const dayEvents   = eventsByDay[selectedKey] || [];

  const prevMonth = () => setAnchor(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setAnchor(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  const goToday   = () => {
    const now = new Date();
    setAnchor(startOfMonth(now));
    const d = new Date(now); d.setHours(0,0,0,0);
    setSelectedDay(d);
  };

  return (
    <>
      <PageHeader
        title="Calendar"
        subtitle="Hearings, tasks and filings across all cases." />

      <div className="bg-muted border border-border">

        {/* ── Month navigation header (all screens) ─────────────────────── */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <button onClick={prevMonth} data-testid="cal-prev-btn"
            className="text-muted-foreground hover:text-primary transition-colors">
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <div className="font-serif text-lg" data-testid="cal-month-label">
            {fmtMonthYear(anchor)}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={goToday} data-testid="cal-today-btn"
              className="text-[11px] text-primary font-medium px-2 py-1 rounded-md hover:bg-primary/10 transition-all">
              Today
            </button>
            <button onClick={nextMonth} data-testid="cal-next-btn"
              className="text-muted-foreground hover:text-primary transition-colors">
              <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* ── Month grid (all screens) ───────────────────────────────────── */}
        <div>
          {/* Day-of-week header */}
          <div className="grid grid-cols-7 text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-border">
            {DAY_FULL.map(d => <div key={d} className="px-1 sm:px-2 py-2 text-center sm:text-left">{d}</div>)}
          </div>

          {/* Calendar cells */}
          <div className="grid grid-cols-7">
            {cells.map((d, i) => {
              if (!d) return <div key={`empty-${i}`} className="border border-background aspect-square bg-background" />;

              const key       = istDateKey(d);
              const evs       = eventsByDay[key] || [];
              const isToday   = key === today;
              const isSel     = key === selectedKey;
              const caseIds   = new Set(evs.filter(e => e.ref_case_id).map(e => e.ref_case_id));
              const caseCount = caseIds.size;
              const kinds     = [...new Set(evs.map(e => e.kind))];

              return (
                <button
                  key={key}
                  onClick={() => {
                    setSelectedDay(new Date(d));
                    if (evs.length > 0 && window.innerWidth >= 640)
                      navigate(`/app/cases?hearing_date=${key}`);
                  }}
                  data-testid={`cal-day-${key}`}
                  title={caseCount > 0 ? `${caseCount} case${caseCount > 1 ? "s" : ""} — click to view` : d.getDate().toString()}
                  className={`border border-background aspect-square text-left p-1 sm:p-2 transition-colors
                    ${evs.length > 0 ? "hover:bg-background hover:ring-1 hover:ring-primary cursor-pointer" : "cursor-default"}
                    ${isToday ? "ring-1 ring-inset ring-primary/40" : ""}
                    ${isSel && !isToday ? "bg-primary/5" : ""}`}>

                  <div className={`text-xs font-medium ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                    {isToday
                      ? <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold">{d.getDate()}</span>
                      : d.getDate()}
                  </div>

                  {caseCount > 0 && (
                    <div className="mt-0.5 sm:mt-1 flex flex-col gap-0.5">
                      <div className="inline-flex items-center gap-0.5 sm:gap-1">
                        <span className="text-[10px] sm:text-[11px] font-semibold text-foreground leading-none">{caseCount}</span>
                        <span className="text-[9px] text-muted-foreground leading-none hidden sm:inline">
                          {caseCount === 1 ? "case" : "cases"}
                        </span>
                      </div>
                      <div className="flex gap-0.5 flex-wrap">
                        {kinds.map(k => (
                          <span key={k} className={`w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full ${KIND_DOT[k] || "bg-muted-foreground"}`} />
                        ))}
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Mobile: selected day detail ───────────────────────────────── */}
        <div className="sm:hidden border-t border-border">
          <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between">
            <div className="text-sm font-semibold text-foreground">
              {selectedDay.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", timeZone: "Asia/Kolkata" })}
            </div>
            {dayEvents.length > 0 && (
              <span className="text-[10px] text-muted-foreground">
                {dayEvents.length} event{dayEvents.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {dayEvents.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center">
              No hearings or tasks scheduled
            </div>
          ) : (
            <div className="divide-y divide-border/40">
              {dayEvents.map(e => (
                <div key={e.id}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-background/60 transition-all"
                  onClick={() => e.ref_case_id && navigate(`/app/cases?hearing_date=${selectedKey}`)}>
                  <div className="flex flex-col items-center gap-1 pt-0.5 shrink-0">
                    <span className={`w-2 h-2 rounded-full ${KIND_DOT[e.kind] || "bg-muted-foreground"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    {e.case_number && (
                      <div className="text-[10px] text-muted-foreground font-mono mb-0.5">{e.case_number}</div>
                    )}
                    <div className="text-sm font-medium text-foreground truncate">{e.title}</div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className={`text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded font-semibold
                        ${e.kind === "hearing" ? "bg-primary/10 text-primary"
                        : e.kind === "task"    ? "bg-emerald-500/10 text-emerald-600"
                        :                        "bg-sky-500/10 text-sky-600"}`}>
                        {KIND_LABEL[e.kind] || e.kind}
                      </span>
                      {e.stage && <span className="text-[11px] text-muted-foreground capitalize">{e.stage}</span>}
                      {e.when && <span className="text-[11px] text-muted-foreground">{fmtTime(e.when)}</span>}
                      {e.court && <span className="text-[11px] text-muted-foreground truncate">{e.court}</span>}
                    </div>
                  </div>
                  {e.ref_case_id && (
                    <span className="text-[11px] text-primary shrink-0 pt-0.5 cursor-pointer">View →</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Legend ────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-4 px-4 py-2.5 border-t border-border">
          {[["hearing","Hearing"],["task","Task"],["case","Filing"]].map(([k,label]) => (
            <div key={k} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <span className={`w-2 h-2 rounded-full ${KIND_DOT[k]}`} />
              {label}
            </div>
          ))}
          <span className="ml-auto text-[10px] text-muted-foreground hidden sm:inline">Click a date to open cases</span>
          <span className="ml-auto text-[10px] text-muted-foreground sm:hidden">Tap a date to view events</span>
        </div>

      </div>
    </>
  );
}

